import { Button, Form, Input, Modal, Switch, message } from "antd";
import React, { useState } from "react";
import { PlusOutlined, CopyOutlined } from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";

const EXCHANGE_KEYS = ["import_products", "export_products", "import_orders", "export_orders"];
function exchangeFlagsToLevel(values) {
  return EXCHANGE_KEYS.filter((k) => !!values?.[k]).length;
}
function exchangeLevelToFlags(level) {
  return {
    import_products: level >= 1,
    export_products: level >= 2,
    import_orders: level >= 3,
    export_orders: level >= 4,
  };
}

/** Переключатель, жёстко привязанный к полю формы (useWatch + setFieldValue) */
function FormBoundSwitch({ form, name, ...rest }) {
  const value = Form.useWatch(name, form);
  return (
    <Switch
      {...rest}
      checked={!!value}
      onChange={(checked) => form.setFieldValue(name, checked)}
    />
  );
}

function CommerceMLCreateExchangeCounter({ form }) {
  const importProducts = Form.useWatch("import_products", form);
  const exportProducts = Form.useWatch("export_products", form);
  const importOrders = Form.useWatch("import_orders", form);
  const exportOrders = Form.useWatch("export_orders", form);
  const currentLevel = exchangeFlagsToLevel({
    import_products: importProducts,
    export_products: exportProducts,
    import_orders: importOrders,
    export_orders: exportOrders,
  });
  const setLevel = (level) => form.setFieldsValue(exchangeLevelToFlags(level));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <Button type="button" size="small" disabled={currentLevel <= 0} onClick={() => setLevel(Math.max(0, currentLevel - 1))}>−</Button>
      <span style={{ minWidth: 56, fontWeight: 500 }}>{currentLevel} из 4</span>
      <Button type="button" size="small" disabled={currentLevel >= 4} onClick={() => setLevel(Math.min(4, currentLevel + 1))}>+</Button>
    </span>
  );
}

function CommerceMLModalCreate({ onSave, token }) {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} скопировано`);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const active = form.getFieldValue("active");
      const import_products = form.getFieldValue("import_products");
      const export_products = form.getFieldValue("export_products");
      const import_orders = form.getFieldValue("import_orders");
      const export_orders = form.getFieldValue("export_orders");
      const payload = {
        name: values.name,
        url: values.url?.trim() || "",
        username: values.username?.trim() || undefined,
        password: values.password || undefined,
        active: active === true,
        import_products: import_products === true,
        export_products: export_products === true,
        import_orders: import_orders === true,
        export_orders: export_orders === true,
      };
      const response = await apiRequests.commerceml.createConnection(payload, token);
      const newRecord = response?.data ?? {};
      form.resetFields();
      setVisible(false);
      onSave?.(newRecord);
      message.success("Подключение создано");
      if (newRecord.username != null || newRecord.password != null) {
        setCreatedCredentials({
          name: newRecord.name,
          username: newRecord.username ?? "",
          password: newRecord.password ?? "",
        });
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Ошибка создания подключения";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setVisible(true)}
      >
        Создать подключение
      </Button>
      <Modal
        title="Создать CommerceML подключение"
        open={visible}
        onCancel={() => {
          setVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            active: false,
            import_products: false,
            export_products: false,
            import_orders: false,
            export_orders: false,
            url: "",
          }}
        >
          <div style={{ marginBottom: 16, padding: "10px 12px", background: "#f0f7ff", borderRadius: 8, fontSize: 13, color: "#333" }}>
            <strong>Подключение к нашей системе:</strong> внешняя система (1С и т.д.) подключается к нам по URL, логину и паролю. У нас забирают заказы, нам отгружают товары.
          </div>

          <Form.Item
            label="Название"
            name="name"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input placeholder="Например: Подключение к 1С" />
          </Form.Item>

          <Form.Item
            label="URL внешней системы (опционально)"
            name="url"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || !String(value).trim()) return Promise.resolve();
                  try {
                    new URL(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error("Введите корректный URL"));
                  }
                },
              },
            ]}
            tooltip="Если наша система инициирует запросы к внешней системе — укажите их URL. Иначе можно оставить пустым."
          >
            <Input placeholder="https://example.com/commerceml" allowClear />
          </Form.Item>

          <Form.Item
            label="Логин"
            name="username"
            tooltip="Необязательно. Если не указать — будет сгенерирован автоматически и показан после создания."
          >
            <Input placeholder="Логин для доступа внешней системы к нам (опционально)" allowClear />
          </Form.Item>

          <Form.Item
            label="Пароль"
            name="password"
            tooltip="Необязательно. Если не указать — будет сгенерирован автоматически и показан после создания."
          >
            <Input.Password placeholder="Пароль (опционально)" allowClear />
          </Form.Item>

          <Form.Item label="Настройки обмена" style={{ marginBottom: 0 }}>
            <Form.Item name="active" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="active" checkedChildren="Активно" unCheckedChildren="Неактивно" />
              <span style={{ marginLeft: 8 }}>Активно</span>
            </Form.Item>

            <Form.Item label="Режим обмена" style={{ marginBottom: 16 }} tooltip="Сколько из четырёх режимов обмена включено">
              <CommerceMLCreateExchangeCounter form={form} />
            </Form.Item>

            <Form.Item name="import_products" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="import_products" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Принимать товары</span>
            </Form.Item>

            <Form.Item name="export_products" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="export_products" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Выгружать товары</span>
            </Form.Item>

            <Form.Item name="import_orders" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="import_orders" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Принимать заказы</span>
            </Form.Item>

            <Form.Item name="export_orders" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="export_orders" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Выгружать заказы</span>
            </Form.Item>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Создать
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Данные для входа в 1С"
        open={!!createdCredentials}
        onCancel={() => setCreatedCredentials(null)}
        footer={
          <Button type="primary" onClick={() => setCreatedCredentials(null)}>
            Закрыть
          </Button>
        }
        width={480}
        closable
      >
        <p style={{ marginBottom: 16, color: "#666" }}>
          Сохраните логин и пароль — они понадобятся для настройки обмена в 1С. Пароль больше не показывается в списке подключений.
        </p>
        {createdCredentials && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {createdCredentials.name && (
              <div>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Подключение</div>
                <strong>{createdCredentials.name}</strong>
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Логин</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Input
                  readOnly
                  value={createdCredentials.username}
                  style={{ flex: 1, fontFamily: "monospace" }}
                />
                <Button
                  type="default"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(createdCredentials.username, "Логин")}
                >
                  Копировать
                </Button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Пароль</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Input.Password
                  readOnly
                  value={createdCredentials.password}
                  style={{ flex: 1, fontFamily: "monospace" }}
                />
                <Button
                  type="default"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(createdCredentials.password, "Пароль")}
                >
                  Копировать
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default CommerceMLModalCreate;
